import React from "react";
import {
  formatMoney, relativeDue, stateOf, formatDate, stateMeta, strengthMeta, fulfilledLabel,
} from "../lib/api";
import { Link2, ShieldQuestion, Check } from "lucide-react";

const Badge = ({ meta, testid }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center px-2 py-[3px] font-mono text-[10px] uppercase tracking-wider rounded-sm"
    style={{ backgroundColor: meta.bg, color: meta.fg }}
  >
    {meta.label}
  </span>
);

const Initials = ({ name }) => {
  const initials = (name || "?")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join("");
  return (
    <div className="h-10 w-10 rounded-sm border border-ledger-border bg-white grid place-items-center font-mono text-xs text-ledger-ink shrink-0">
      {initials}
    </div>
  );
};

export const CommitmentRow = ({ commitment, onOpen }) => {
  const state = stateOf(commitment);
  const isFinancial = commitment.amount != null;
  const isFulfilled = commitment.status === "fulfilled";
  const displayName =
    commitment.direction === "to_me" ? commitment.promisor_name : commitment.promisee_name;
  const org = commitment.promisor_organization;
  const strength = commitment.strength || "strong";
  const fLabel = fulfilledLabel(commitment);

  return (
    <button
      onClick={() => onOpen(commitment)}
      data-testid={`commitment-row-${commitment.id}`}
      className={
        "group w-full text-left border-b border-ledger-border py-5 px-4 md:px-5 flex items-start gap-4 " +
        "transition-transform duration-200 ease-out hover:-translate-y-[1px] hover:bg-white " +
        (isFinancial ? "bg-[#FFFDF6]" : "bg-transparent") +
        (isFulfilled ? " opacity-90" : "")
      }
    >
      <Initials name={displayName} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-sans text-sm font-semibold text-ledger-ink" data-testid="row-person">
            {displayName}
          </span>
          {org && <span className="text-xs text-ledger-muted font-mono">· {org}</span>}
          <Badge meta={stateMeta[state] || stateMeta.open} testid={`state-badge-${state}`} />
          {strength !== "strong" && strength !== state && (
            <Badge meta={strengthMeta[strength]} testid={`strength-badge-${strength}`} />
          )}
          {commitment.source_type === "gmail" && (
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-ledger-muted">
              <Link2 className="h-3 w-3" /> gmail
            </span>
          )}
        </div>
        <div className={"mt-1 text-sm line-clamp-2 " + (isFulfilled ? "text-ledger-sub line-through decoration-ledger-muted/40" : "text-ledger-sub")} data-testid="row-promise">
          {commitment.promise_text}
        </div>
        {commitment.condition && !isFulfilled && (
          <div className="mt-1 text-[12px] text-[#6D28D9] font-mono">
            <span className="opacity-60">if:</span> {commitment.condition}
          </div>
        )}
        <div className="mt-2 flex items-center gap-3 font-mono text-[11px] text-ledger-muted flex-wrap">
          {isFulfilled ? (
            <span className="inline-flex items-center gap-1.5 text-[#047857]" data-testid="row-fulfilled-label">
              <Check className="h-3 w-3" />
              {fLabel}
              {commitment.fulfilled_at && (
                <span className="text-ledger-muted"> · {formatDate(commitment.fulfilled_at)}</span>
              )}
            </span>
          ) : (
            <>
              <span>{formatDate(commitment.promised_at)}</span>
              <span>→</span>
              {commitment.expected_completion ? (
                <span
                  className={
                    state === "overdue"
                      ? "text-[#B91C1C]"
                      : state === "due_soon" || state === "due_today"
                      ? "text-[#92400E]"
                      : ""
                  }
                >
                  {formatDate(commitment.expected_completion)} · {relativeDue(commitment.expected_completion)}
                  {commitment.deadline_certainty === "inferred" && (
                    <span className="ml-1 opacity-60">(inferred)</span>
                  )}
                </span>
              ) : commitment.timeframe_text ? (
                <span className="inline-flex items-center gap-1 text-ledger-sub">
                  <ShieldQuestion className="h-3 w-3" /> {commitment.timeframe_text}
                </span>
              ) : (
                <span>no due date</span>
              )}
            </>
          )}
          <span className="ml-auto">
            confidence {Math.round((commitment.confidence || 0) * 100)}%
          </span>
        </div>
      </div>
      {isFinancial && (
        <div
          data-testid="row-amount"
          className={
            "ml-2 shrink-0 self-center font-mono text-lg px-3 py-1 rounded-sm border " +
            (isFulfilled
              ? "text-[#047857] bg-[#D1FAE5] border-[#10B981]"
              : "text-[#78350F] bg-ledger-goldBg border-ledger-goldBorder")
          }
        >
          {formatMoney(commitment.amount, commitment.currency)}
        </div>
      )}
    </button>
  );
};
